
import { Navigation, Manipulation, Pagination, Thumbs, Keyboard } from 'swiper/modules';
import { addSlider } from '../helpers';

// We need this in case WooCommerce fires the event before the page is fully loaded.
let initialVariation = null;

class SingleProductSlider {
	/**
	 * Have to continue using jQuery as the way variations work in WC
	 * means that you can only listen to the custom variation events
	 * with it.
	 */
	constructor() {
		const images = document.querySelectorAll( '.product-image-slider-item' );
		this.imageList = this._generateProductList( images );

		const thumbImages = document.querySelectorAll( '.product-images-slider-item-thumb' );
		this.thumbList = this._generateProductList( thumbImages );

		this.price = document.querySelector( '.summary-inner-wrapper > .price' );
		this.originalPrice = this.price.cloneNode( true );

		const productSliderContainer = document.querySelector( '.product-images-slider-container' );

		const thumbnailSliderPositionMobile = productSliderContainer?.dataset?.thumbnailPositionMobile ?? 'right';
		const thumbnailSliderPositionDesktop = productSliderContainer?.dataset?.thumbnailPositionDesktop ?? 'left';

		let thumbnailSliderDirectionMobile = 'vertical';
		if ( 'top' === thumbnailSliderPositionMobile || 'bottom' === thumbnailSliderPositionMobile ) {
			thumbnailSliderDirectionMobile = 'horizontal';
		}

		let thumbnailSliderDirectionDesktop = 'vertical';
		if ( 'top' === thumbnailSliderPositionDesktop || 'bottom' === thumbnailSliderPositionDesktop ) {
			thumbnailSliderDirectionDesktop = 'horizontal';
		}

		const thumbnailSliderSettings = {
			modules: [ Navigation, Manipulation ],
			threshold: 1,
			slidesPerView: 'auto',
			direction: thumbnailSliderDirectionMobile,
			autoHeight: true,
			freeMode: true,
			watchSlidesVisibility: true,
			watchSlidesProgress: true,
			slideToClickedSlide: true,
			allowTouchMove: true,
			breakpoints: {
				922: {
					direction: thumbnailSliderDirectionDesktop,
				},
			},
		};

		this.disableThumbnails = document.querySelector( '.product-image-slider[data-slider-no-thumbs]' );
		let thumbs = '';

		if ( ! this.disableThumbnails ) {
			this.thumbnailSlider = addSlider( '.product-images-slider-thumbnails', thumbnailSliderSettings )[ 0 ];
			thumbs = {
				swiper: this.thumbnailSlider,
				slideThumbActiveClass: 'product-images-slider-item-thumb--selected',
			};
		}

		const paginationEl = document.querySelector( '.swiper-pagination' );
		let pagination = '';

		if ( paginationEl ) {
			pagination = {
				el: '.swiper-pagination',
				type: 'bullets',
				clickable: true,
			};
		}
		const carousel = document.querySelector( '.product-image-slider' );
		const slidesShown = parseInt( carousel?.dataset?.sliderType, 10 ) || 1;

		// Set default settings
		let sliderLoop = false;
		let sliderObserver = false;

		// If slider is set to show more then 1 slide, we need to enable loop and observer
		if ( slidesShown > 1 ) {
			sliderLoop = true;
			sliderObserver = true;
		}

		// if slidesShown is 2 and there are 2 or less images, there is a console warning about swiper's loop mode, so disable loop
		const slideCount = carousel.querySelectorAll( '.product-image-slider-item' ).length;
		if ( sliderLoop && slideCount <= slidesShown ) {
			sliderLoop = false;
		}

		const sliderKeyboardDisabled = window?.qalaThemeData?.swiperKeyboardDisabled;

		const productSliderSettings = {
			modules: [ Manipulation, Pagination, Thumbs, Keyboard ],
			slidesPerView: 'auto',
			autoHeight: true,
			direction: 'horizontal',
			loop: sliderLoop,
			observer: sliderObserver,
			observeParents: true,
			freeMode: true,
			autoplay: {
				delay: 5000,
			},
			keyboard: {
				enabled: sliderKeyboardDisabled ? false : true,
			},
			pagination,
			thumbs,
			breakpoints: {
				992: {
					slidesPerView: slidesShown,
				},
			},
			on: {
				init: () => {
					this._iframeDataSrcSwap();
				},
				breakpoint: () => {
					this._iframeDataSrcSwap();
				},
				orientationChange: () => {
					this._iframeDataSrcSwap();
				},
				slideChangeTransitionEnd() {
					const activeSlideCaption = document.querySelector( '.product-image-slider-item.swiper-slide-active img' )?.dataset?.caption;
					const productImageCaption = document.querySelector( '.product-image-caption' );

					if ( productImageCaption ) {
						if ( activeSlideCaption ) {
							productImageCaption.innerText = activeSlideCaption;
						} else if ( ! activeSlideCaption ) {
							productImageCaption.innerText = '';
						}
					}
				},
				slideChange() {
					const youtubeSlides = document.querySelectorAll( '.product-image-slider .video-slide iframe[src*="youtube.com"]' );

					if ( youtubeSlides ) {
						youtubeSlides.forEach( videoSlide => {
							videoSlide.contentWindow.postMessage( '{"event":"command","func":"pauseVideo","args":""}', '*' );
						} );
					}
				},
			},
		};
		this.productImageSlider = addSlider( '.product-image-slider', productSliderSettings )[ 0 ];
	}

	init() {
		this._addVariationListener();
		this._addResetListener();
		this._getVariationOnPageLoad();
		this._removeBundleThumbAttr();
	}

	/**
	 * Resets sliders to contain original images.
	 *
	 * @private
	 */
	_resetSliders = () => {
		const productImageElements = this.imageList.map( image => image.element );
		const thumbnailElements = this.thumbList.map( image => image.element );

		this.productImageSlider.appendSlide( productImageElements );

		if ( ! this.disableThumbnails ) {
			this.thumbnailSlider.appendSlide( thumbnailElements );

			this.thumbnailSlider.slideTo( 0, 0 );

			// reset selected class of all but first thumb
			const thumbImages = document.querySelectorAll( '.product-images-slider-item-thumb--selected' );
			thumbImages.forEach( ( thumbs, index ) => {
				if ( index < 1 ) {
					return;
				}
				thumbs.classList.remove( 'product-images-slider-item-thumb--selected' );
			} );
		}

		this._updatePrice();
	};

	/**
	 * Calculates if any images need to be added to a product variation slider, or removed
	 * from the slider if product images exist which aren't part of the selected variation
	 *
	 * @param {Object} variation - variation object given from WC variation event
	 * @private
	 */
	_changeProductImage( variation ) {
		const newProductImageSlides = [];
		const newThumbnailImageSlides = [];
		const slidesToRemove = [];
		const sliderContainer = document.querySelector( '.product-images-slider-container' );
		let slidesToRemoveAttributeData = [];
		const attrSlidesToRemove = sliderContainer.getAttribute( 'data-slides-to-remove' );

		// Make sure this exists before trying to use it.
		if ( attrSlidesToRemove ) {
			slidesToRemoveAttributeData = Array.from( attrSlidesToRemove.split( ',' ), Number );
		}

		const removeSlides = slides => {
			this.productImageSlider.removeSlide( slides );
			if ( ! this.disableThumbnails ) {
				this.thumbnailSlider.removeSlide( slides );
			}

			this.productImageSlider.update();
			if ( ! this.disableThumbnails ) {
				this.thumbnailSlider.update();
			}
		};

		this.imageList.forEach( ( image, index ) => {
			// check if key value pair matches in two objects
			function hasSameProperties( obj1, obj2 ) {
				for ( const key in obj1 ) {
					if ( obj1.hasOwnProperty( key ) ) {
						if ( obj1[ key ] !== obj2[ key ] ) {
							return false;
						}
					}
				}
				for ( const key in obj2 ) {
					if ( obj2.hasOwnProperty( key ) ) {
						// obj2 is always the variation object. Variations set to have ANY term are blank, so here we
						// first check if the value is blank and return true. if it isn't then the usual check applies.
						// the key exists and the value is blank ONLY for variation product images with that attribute set to Any attribute
						if ( obj2[ key ] === '' ) {
							return true;
						}
						if ( obj1[ key ] !== obj2[ key ] ) {
							return false;
						}
					}
				}
				return true;
			}

			// check if key value pair in object.
			if ( hasSameProperties( image.attributes, variation.attributes ) ) {
				newProductImageSlides.push( image.element );
				if ( ! this.disableThumbnails ) {
					const thumbnailElement = this.thumbList[ index ].element;
					newThumbnailImageSlides.push( thumbnailElement );
				}
			} else if ( ! Array.isArray( image.attributes ) ) {
				// slide is specific to a product variation image, so remove it
				slidesToRemove.push( index );
			}
		} );

		if ( newProductImageSlides.length !== 0 ) {
			this._alterSliderBasedOnImages( newProductImageSlides );

			// Build slider with matching variation images
			// NEEDS to be in this order, else thumb select won't work
			this.productImageSlider.removeAllSlides();
			if ( ! this.disableThumbnails ) {
				this.thumbnailSlider.removeAllSlides();
			}

			newProductImageSlides.forEach( ( slide, index ) => {
				this.productImageSlider.addSlide( index, slide );
			} );

			if ( ! this.disableThumbnails ) {
				newThumbnailImageSlides.forEach( ( slide, index ) => {
					this.thumbnailSlider.addSlide( index, slide );
				} );
			}

			this.productImageSlider.update();
			if ( ! this.disableThumbnails ) {
				this.thumbnailSlider.update();
			}
		} else if ( newProductImageSlides.length === 0 ) { // if the selected variants product has no photo
			this._resetSliders();
			removeSlides( slidesToRemove );
		} else if (
			JSON.stringify( slidesToRemoveAttributeData ) !== JSON.stringify( slidesToRemove )
		) {
			// Remove only variation images
			removeSlides( slidesToRemove );
		}

		sliderContainer.setAttribute( 'data-slides-to-remove', slidesToRemove );
	}

	/**
	 * Alter slider based on number of images and preset data
	 *
	 * @param {Object} newProductImageSlides
	 * @private
	 */
	_alterSliderBasedOnImages( newProductImageSlides ) {
		// Get slider and how many slides to show
		const carousel = document.querySelector( '.product-image-slider' );
		let slidesShown = parseInt( carousel?.dataset?.sliderType, 10 ) || 1;

		// If we are trying to show more slides then we have, and view is not auto, set to match length
		if ( slidesShown > newProductImageSlides.length ) {
			slidesShown = newProductImageSlides.length;
		}

		// Only change if slidesPerView is specifically set, this also acts as reset if previous slide changed the param
		if ( this.productImageSlider.params.slidesPerView !== 'auto' ) {
			this.productImageSlider.params.slidesPerView = slidesShown;
		}

		// Update settings
		this.productImageSlider.update();
	}

	/**
	 * Updates price from a variation
	 *
	 * @param {Object} [variation] - variation object given from WC variation event
	 * @private
	 */
	_updatePrice( variation ) {
		if ( ! variation ) {
			this.price.innerHTML = this.originalPrice.innerHTML;
			return;
		}

		/* price_html returns empty if variation price is same as previous variation */
		if ( variation.price_html === '' ) {
			return;
		}

		this.price.innerHTML = variation.price_html;
	}

	/**
	 * Adds WC variation listener for data reset
	 *
	 * @private
	 */
	_addResetListener() {
		jQuery( '.variations_form' ).on( 'reset_data', this._resetSliders );
	}

	/**
	 * Adds WC variation listener for variation change
	 *
	 * @private
	 */
	_addVariationListener() {
		jQuery( '.variations_form' ).on( 'found_variation', ( event, variation ) => {
			this._changeProductImage( variation );
			this._updatePrice( variation );

			// We can reset it here. Cause if this runs, it means everything is fine
			// and we don't have to run the initial setup a few lines below.
			if ( initialVariation ) {
				initialVariation = null;
			}
		} );

		// If there is an initial variation, set the image and price on page load. This would happen if the above event
		// is fired before the page is fully loaded.
		if ( initialVariation ) {
			this._changeProductImage( initialVariation );
			this._updatePrice( initialVariation );
		}
	}

	/**
	 * Checks the current URL parameters for variations and attempts to set the image slider on page load using those.
	 *
	 * @private
	 */
	_getVariationOnPageLoad() {
		const currentURL = new URL( window.location.href );
		const currentURLParams = new URLSearchParams( currentURL.search )?.entries() ?? false;
		if ( ! currentURLParams ) {
			return;
		}
		const currentVariations = {
			attributes: {},
		};
		for ( const [ key, value ] of currentURLParams ) {
			if ( key.startsWith( 'attribute_' ) ) {
				currentVariations.attributes[ key ] = value;
			}
		}

		if ( Object.entries( currentVariations.attributes ).length !== 0 ) {
			this._changeProductImage( currentVariations );
		}
	}

	/**
	 * Generates a list of products containing
	 * elements and variation data that was provided
	 * from the DOM
	 *
	 * @typedef {Object} productListItem
	 * @property {Object}             attributes - things
	 * @property {HTMLElement}        element    - html element of product image
	 *
	 * @param    {Array[HTMLElement]} collection - Array of product elements
	 * @return {Array[productListItem]} - List of objects containing elements and variation data
	 * @private
	 */
	_generateProductList( collection ) {
		const collectionList = [];
		collection.forEach( image => {
			collectionList.push( {
				element: image,
				attributes: JSON.parse( image.dataset.attributes ),
			} );
		} );
		return collectionList;
	}

	/**
	 * Removes class from bundle theme so that image does not get replaced.
	 *
	 * @private
	 */
	_removeBundleThumbAttr() {
		const bundledProductImg = document.querySelector( '.bundled_product_image' );
		if ( bundledProductImg ) {
			bundledProductImg.classList.remove( 'woocommerce-product-gallery__image' );
		}
	}

	/**
	 * Replaces `data-src` with `src` for any gallery video iframe elements. So initial loading perf is better.
	 *
	 * Complianz uses it's own `data-src-cmplz` attribute for cookie support. It also changes our data-src
	 * attribute to `data- src`, so when Complianz is active this code does not run.
	 *
	 * @private
	 */
	_iframeDataSrcSwap() {
		const videoSlides = document.querySelectorAll( '.product-image-slider .video-slide iframe[data-src]' );
		if ( videoSlides ) {
			videoSlides.forEach( videoSlide => {
				const videoSrc = videoSlide.dataset?.src;
				videoSlide.setAttribute( 'src', videoSrc );
			} );
		}
	}
}

class ProductVariations {
	/**
	 * Have to continue using jQuery as the way variations work in WC
	 * means that you can only listen to the custom variation events
	 * with it.
	 */
	constructor() {
		const attributeNames = [];
		const attributeSingleValues = [];
		const variationAttributes = document.querySelectorAll( '.woo-variation-raw-select' );

		variationAttributes.forEach( variationAttribute => {
			attributeNames.push( variationAttribute.name );
		} );

		this.attributeNames = attributeNames;

		attributeNames.forEach( attributeName => {
			let attributeValues = '';
			// if a swatch picker exists for this attribute, we need to use that for later logic
			// as it is an <a> element with different attributes to the default <li>
			const swatch = document.querySelector( '#picker_' + attributeName.slice( 10 ) );
			if ( swatch ) {
				attributeValues = document.querySelectorAll( '[data-attribute="' + attributeName.slice( 10 ) + '"] a' );
			} else {
				attributeValues = document.querySelectorAll( '[data-attribute_name="' + attributeName + '"] li' );
			}
			attributeSingleValues.push( attributeValues );
		} );

		this.attributeSingleValues = attributeSingleValues;
	}

	init() {
		this._addAttributeListener( this.attributeSingleValues );
	}

	/**
	 * Adds WC variation listener for variation select
	 *
	 * @param {Array} attributeSingleValues
	 *
	 * @private
	 */
	_addAttributeListener( attributeSingleValues ) {
		attributeSingleValues.forEach( ( attributeSingleValueList, index ) => {
			attributeSingleValueList.forEach( attributeSingleValue => {
				attributeSingleValue.addEventListener( 'click', () => {
					this._createQueryString( this.attributeNames[ index ], attributeSingleValue );
				} );
			} );
		} );
	}

	/**
	 * Creates the query string with values
	 *
	 * @param {string}      attributeName
	 * @param {HTMLElement} attributeSingleValue
	 *
	 * @private
	 */
	_createQueryString( attributeName, attributeSingleValue ) {
		let attributeActive = '';
		let attributeValue = '';
		const swatch = document.querySelector( '#picker_' + attributeName.slice( 10 ) );

		if ( swatch ) {
			attributeActive = attributeSingleValue.parentElement.classList.contains( 'selected' );
			attributeValue = attributeSingleValue.parentElement.getAttribute( 'data-value' );
		} else {
			attributeActive = attributeSingleValue.getAttribute( 'aria-checked' ) === 'true';
			attributeValue = attributeSingleValue.getAttribute( 'data-value' );
		}

		const url = new URL( window.location.href );
		const params = new URLSearchParams( url.search );

		if ( attributeActive === false ) {
			if ( params.has( attributeName ) ) {
				params.set( attributeName, attributeValue );
			} else {
				params.append( attributeName, attributeValue );
			}
		} else {
			params.delete( attributeName, attributeValue );
		}

		const newURL = window.location.protocol + '//' + window.location.host + window.location.pathname + '?' + params.toString();
		window.history.pushState( { path: newURL }, '', newURL );
	}
}

window.onload = () => {
	// Initialize slider only if there is a slider container.
	const imagesSliderContainer = document.querySelector( '.product-images-slider-container' );
	if ( imagesSliderContainer ) {
		const images = document.querySelectorAll( '.product-image-slider-item' );

		// if there are more than 1 images, initialize swiper sliders:
		if ( images.length > 1 ) {
			const singleProductSlider = new SingleProductSlider();
			singleProductSlider.init();
		}

		const variationsForm = document.querySelector( '.variations_form' );

		if ( null === variationsForm ) {
			return;
		}

		const productVariations = new ProductVariations();
		productVariations.init();
	}
};

jQuery( '.variations_form' ).on( 'found_variation', ( event, variation ) => {
	initialVariation = variation;
} );
